/**
 * Daily Challenges — home-screen widget.
 *
 * A collapsible card in HomeScreen's `.home-progression-slot` (the slot the
 * Tutorial Part 2 task board retires out of). Shows the 11 static daily
 * challenges across 3 pages (The Streets / Cee-Lo / Craps) with per-level
 * gold collects.
 *
 * Pure visual representation of server state (`DailyChallengesState`):
 * progress only ever arrives via the auth snapshot and the
 * `daily_challenges:update` push — the widget never counts anything itself.
 * The only outbound event is `claim` (the collect tap), which main.ts relays
 * to the server for atomic validation.
 *
 * Collapse contract (owner-specified):
 *  - Starts COLLAPSED by default (inverse of the task board).
 *  - While collapsed, the header's gold claim pill is the only "you have
 *    gold waiting" signal (an under-header collect tray was built and then
 *    cut — owner preferred the pill alone). Collecting happens on the
 *    COLLECT rows after expanding.
 */

import { EventEmitter } from '../../engine/core/EventEmitter';
import { attachSwipePager } from './swipePager';
import {
  CHALLENGE_PAGE_TITLES,
  DAILY_CHALLENGES,
  challengeLevelForProgress,
  unclaimedGold,
  type DailyChallengeDef,
  type DailyChallengeEntry,
  type DailyChallengeId,
  type DailyChallengesState,
} from '@sdl/shared';

export interface ChallengeClaimRequest {
  challengeId: DailyChallengeId;
  /** Gold the tap is expected to collect (display only — server re-validates). */
  gold: number;
  /** The tapped element, for the coin flyout origin. */
  button: HTMLElement;
}

const COLLAPSE_KEY = 'sdl_dc_collapsed';
const GOLD_ICON = '/assets/icons/currency/gold.svg';

interface RowView {
  def: DailyChallengeDef;
  entry: DailyChallengeEntry;
  doneLevels: number;
  claimed: number;
  claimableGold: number;
  maxed: boolean;
}

export class DailyChallengesWidget extends EventEmitter {
  public element: HTMLElement;
  private rowsEl: HTMLElement;
  private titleEl: HTMLElement;
  private claimPillEl: HTMLElement;
  private dotsEl: HTMLElement;
  private bodyEl: HTMLElement;
  private caretEl: HTMLElement;

  private state: DailyChallengesState | null = null;
  private currentPage = 0;
  private userPicked = false;
  private collapsed = true;
  /** Challenges with a claim tap in flight — rendered disabled until the next server state lands. */
  private pendingClaims = new Set<DailyChallengeId>();

  constructor() {
    super();
    this.injectStyles();
    // Default COLLAPSED: only an explicit expand ('0') persists open.
    this.collapsed = localStorage.getItem(COLLAPSE_KEY) !== '0';

    this.element = document.createElement('div');
    this.element.className = 'sdl-dailych';
    // Touches starting here must never page the SCREEN pager (UIManager
    // locks gestures on [data-no-swipe] out of navigation); the widget
    // pages itself via attachSwipePager below.
    this.element.dataset.noSwipe = 'true';
    this.element.style.display = 'none'; // hidden until applyState + setVisible
    this.element.innerHTML = `
      <button class="sdl-dc-header" type="button">
        <div class="sdl-dc-titlewrap">
          <span class="sdl-dc-kicker">DAILY CHALLENGES</span>
          <span class="sdl-dc-title"></span>
        </div>
        <span class="sdl-dc-claimpill"></span>
        <span class="sdl-dc-caret">▾</span>
      </button>
      <div class="sdl-dc-body">
        <div class="sdl-dc-rows"></div>
        <div class="sdl-dc-foot">
          <div class="sdl-dc-dots"></div>
          <div class="sdl-dc-hint">Levels pay 10, 15 and 25 gold</div>
        </div>
      </div>
    `;

    this.titleEl = this.element.querySelector('.sdl-dc-title')!;
    this.claimPillEl = this.element.querySelector('.sdl-dc-claimpill')!;
    this.rowsEl = this.element.querySelector('.sdl-dc-rows')!;
    this.dotsEl = this.element.querySelector('.sdl-dc-dots')!;
    this.bodyEl = this.element.querySelector('.sdl-dc-body')!;
    this.caretEl = this.element.querySelector('.sdl-dc-caret')!;

    this.element.querySelector('.sdl-dc-header')!.addEventListener('click', () => this.toggleCollapse());
    attachSwipePager(this.bodyEl, (dir) => this.swipePage(dir));
    this.applyCollapsed(false);
    // Foreground heal: rebuilding the rows forces the WebView to repaint the
    // subtree, recovering from any stale blank left while backgrounded.
    document.addEventListener('visibilitychange', this.onVisibilityChange);
  }

  private onVisibilityChange = (): void => {
    if (document.visibilityState === 'visible' && this.state) this.render();
  };

  /** Swipe between pages with a small slide-in beat. */
  private swipePage(dir: 1 | -1): void {
    const next = this.currentPage + dir;
    if (next < 0 || next >= CHALLENGE_PAGE_TITLES.length) return;
    this.userPicked = true;
    this.currentPage = next;
    this.render();
    const anim = this.rowsEl.animate(
      [
        { transform: `translateX(${dir * 18}px)`, opacity: 0.3 },
        { transform: 'translateX(0)', opacity: 1 },
      ],
      { duration: 200, easing: 'ease-out' },
    );
    // Samsung WebView can drop the box's paint when this animation's
    // temporary compositor layer is torn down (DOM + layout intact, rows
    // render blank). Dirty the element for one frame after the layer is
    // gone so the region is repainted in place.
    anim.onfinish = () => {
      this.rowsEl.style.opacity = '0.999';
      requestAnimationFrame(() => { this.rowsEl.style.opacity = ''; });
    };
  }

  /** Re-render from authoritative server state. Does NOT change visibility. */
  applyState(state: DailyChallengesState): void {
    // A fresh server state answers any in-flight claim taps.
    this.pendingClaims.clear();
    this.state = state;
    if (!this.userPicked) {
      const claimPage = this.firstClaimablePage();
      if (claimPage !== null) this.currentPage = claimPage;
    }
    this.render();
  }

  setVisible(visible: boolean): void {
    this.element.style.display = visible ? '' : 'none';
  }

  private view(def: DailyChallengeDef): RowView {
    const entry = this.state?.challenges.find((c) => c.id === def.id)
      ?? { id: def.id, progress: 0, claimed: 0 };
    const doneLevels = challengeLevelForProgress(def, entry.progress);
    const claimableGold = this.pendingClaims.has(def.id)
      ? 0
      : unclaimedGold(def, entry.progress, entry.claimed);
    return {
      def,
      entry,
      doneLevels,
      claimed: entry.claimed,
      claimableGold,
      maxed: entry.claimed >= def.thresholds.length,
    };
  }

  private claimables(): RowView[] {
    return DAILY_CHALLENGES.map((def) => this.view(def)).filter((v) => v.claimableGold > 0);
  }

  private firstClaimablePage(): number | null {
    const first = this.claimables()[0];
    return first ? first.def.page : null;
  }

  private requestClaim(v: RowView, button: HTMLElement): void {
    if (v.claimableGold <= 0 || this.pendingClaims.has(v.def.id)) return;
    this.pendingClaims.add(v.def.id);
    const req: ChallengeClaimRequest = { challengeId: v.def.id, gold: v.claimableGold, button };
    this.emit('claim', req);
    // Re-render immediately so the tapped control drops its claimable state;
    // the server's daily_challenges:update then re-renders the truth.
    this.render();
  }

  private render(): void {
    if (!this.state) return;

    this.titleEl.textContent = CHALLENGE_PAGE_TITLES[this.currentPage] ?? '';

    // Header pill: number of challenges with gold waiting.
    const claimables = this.claimables();
    if (claimables.length > 0) {
      this.claimPillEl.innerHTML = `<img src="${GOLD_ICON}" alt=""><span>${claimables.length}</span>`;
      this.claimPillEl.classList.add('visible');
    } else {
      this.claimPillEl.innerHTML = '';
      this.claimPillEl.classList.remove('visible');
    }

    this.renderRows();
    this.renderDots();
  }

  private renderRows(): void {
    this.rowsEl.innerHTML = '';
    const pageDefs = DAILY_CHALLENGES.filter((d) => d.page === this.currentPage);
    for (const def of pageDefs) {
      const v = this.view(def);
      const row = document.createElement('div');
      row.className = `sdl-dc-row${v.maxed ? ' maxed' : ''}${v.claimableGold > 0 ? ' claimable' : ''}`;

      const levels = def.thresholds.length;
      const pips = Array.from({ length: levels }, (_, i) => {
        const cls = i < v.claimed ? 'claimed' : (i < v.doneLevels ? 'ready' : '');
        return `<span class="sdl-dc-pip ${cls}"></span>`;
      }).join('');

      let right: string;
      if (v.claimableGold > 0) {
        right = `<button class="sdl-dc-collect" type="button" data-sound="none">
          <img src="${GOLD_ICON}" alt=""><span>+${v.claimableGold}</span><em>COLLECT</em>
        </button>`;
      } else if (v.maxed) {
        right = `<span class="sdl-dc-maxed">✓</span>`;
      } else {
        const target = def.thresholds[v.doneLevels];
        const pct = Math.max(0, Math.min(100, Math.round((v.entry.progress / target) * 100)));
        right = `<span class="sdl-dc-progresswrap">
          <span class="sdl-dc-count">${v.entry.progress}/${target}</span>
          <span class="sdl-dc-bar"><span class="sdl-dc-fill" style="width:${pct}%"></span></span>
        </span>`;
      }

      row.innerHTML = `
        <span class="sdl-dc-icon">${v.maxed ? '✓' : def.icon}</span>
        <span class="sdl-dc-text">
          <span class="sdl-dc-rowtitle">${def.title}</span>
          <span class="sdl-dc-rowsub">
            <span class="sdl-dc-rowdesc">${def.desc}</span>
            <span class="sdl-dc-pips">${pips}</span>
          </span>
        </span>
        ${right}
      `;

      const collectBtn = row.querySelector<HTMLElement>('.sdl-dc-collect');
      collectBtn?.addEventListener('click', (e) => {
        e.stopPropagation();
        this.requestClaim(this.view(def), collectBtn);
      });
      this.rowsEl.appendChild(row);
    }
  }

  private renderDots(): void {
    this.dotsEl.innerHTML = '';
    CHALLENGE_PAGE_TITLES.forEach((_, i) => {
      const pageDefs = DAILY_CHALLENGES.filter((d) => d.page === i);
      const allMaxed = pageDefs.every((d) => this.view(d).maxed);
      const hasClaim = pageDefs.some((d) => this.view(d).claimableGold > 0);
      const dot = document.createElement('button');
      dot.type = 'button';
      dot.className = `sdl-dc-dot${i === this.currentPage ? ' active' : ''}${allMaxed ? ' complete' : ''}${hasClaim ? ' hasclaim' : ''}`;
      dot.addEventListener('click', () => {
        this.userPicked = true;
        this.currentPage = i;
        this.render();
      });
      this.dotsEl.appendChild(dot);
    });
  }

  private toggleCollapse(): void {
    this.collapsed = !this.collapsed;
    localStorage.setItem(COLLAPSE_KEY, this.collapsed ? '1' : '0');
    this.applyCollapsed(true);
  }

  /** Apply collapse state. `animate=false` commits instantly (initial mount). */
  private applyCollapsed(animate: boolean): void {
    this.caretEl.textContent = this.collapsed ? '▸' : '▾';
    if (!animate) {
      this.bodyEl.style.transition = 'none';
      this.bodyEl.style.maxHeight = this.collapsed ? '0px' : '';
      this.bodyEl.style.opacity = this.collapsed ? '0' : '1';
      void this.bodyEl.offsetHeight;
      requestAnimationFrame(() => { this.bodyEl.style.transition = ''; });
      return;
    }
    if (this.collapsed) {
      this.bodyEl.style.maxHeight = `${this.bodyEl.scrollHeight}px`;
      void this.bodyEl.offsetHeight;
      this.bodyEl.style.maxHeight = '0px';
      this.bodyEl.style.opacity = '0';
    } else {
      this.bodyEl.style.maxHeight = `${this.bodyEl.scrollHeight}px`;
      this.bodyEl.style.opacity = '1';
      window.setTimeout(() => {
        if (!this.collapsed) this.bodyEl.style.maxHeight = '';
      }, 320);
    }
  }

  private injectStyles(): void {
    if (document.getElementById('sdl-dailych-styles')) return;
    const styles = document.createElement('style');
    styles.id = 'sdl-dailych-styles';
    styles.textContent = `
      .sdl-dailych {
        background: var(--glass);
        border: 1px solid var(--glass-border);
        border-radius: 14px;
        overflow: hidden;
      }

      .sdl-dc-header {
        display: flex;
        align-items: center;
        gap: 10px;
        width: 100%;
        padding: 12px 14px;
        background: linear-gradient(135deg, rgba(255,215,0,0.10), rgba(255,215,0,0.02));
        border: none;
        cursor: pointer;
        -webkit-tap-highlight-color: transparent;
        text-align: left;
      }
      .sdl-dc-titlewrap { display: flex; flex-direction: column; gap: 2px; flex: 1; min-width: 0; }
      .sdl-dc-kicker {
        font-size: 9px;
        letter-spacing: 1.5px;
        color: rgba(255,255,255,0.45);
        font-weight: 700;
      }
      .sdl-dc-title {
        font-family: var(--font-display);
        font-size: 17px;
        letter-spacing: 1.5px;
        color: var(--gold);
      }
      .sdl-dc-claimpill {
        display: none;
        align-items: center;
        gap: 5px;
        font-family: var(--font-display);
        font-size: 15px;
        color: var(--gold);
        background: rgba(0,0,0,0.35);
        border: 1px solid rgba(255,215,0,0.45);
        border-radius: 12px;
        padding: 2px 10px;
      }
      .sdl-dc-claimpill.visible { display: flex; animation: dcPillPulse 2s ease-in-out infinite; }
      .sdl-dc-claimpill img { width: 14px; height: 14px; }
      @keyframes dcPillPulse {
        0%, 100% { box-shadow: 0 0 0 rgba(255,215,0,0); }
        50% { box-shadow: 0 0 10px rgba(255,215,0,0.35); }
      }
      .sdl-dc-caret { font-size: 12px; color: rgba(255,255,255,0.5); width: 14px; text-align: center; }

      .sdl-dc-body {
        max-height: 800px;
        opacity: 1;
        overflow: hidden;
        transition: max-height 0.3s ease, opacity 0.3s ease;
      }

      .sdl-dc-rows { display: flex; flex-direction: column; padding: 6px; gap: 4px; }
      .sdl-dc-row {
        display: flex;
        align-items: center;
        gap: 12px;
        width: 100%;
        padding: 9px 10px;
        background: rgba(255,255,255,0.02);
        border: 1px solid transparent;
        border-radius: 10px;
      }
      .sdl-dc-row.claimable { background: rgba(255,215,0,0.05); border-color: rgba(255,215,0,0.3); }
      .sdl-dc-row.maxed { opacity: 0.55; }

      .sdl-dc-icon {
        flex-shrink: 0;
        width: 32px;
        height: 32px;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 18px;
        background: rgba(0,0,0,0.3);
        border: 1px solid var(--glass-border);
        border-radius: 9px;
      }
      .sdl-dc-row.maxed .sdl-dc-icon {
        background: rgba(0, 245, 160, 0.12);
        border-color: rgba(0, 245, 160, 0.5);
        color: var(--green, #00f5a0);
        font-weight: 700;
      }

      .sdl-dc-text { display: flex; flex-direction: column; gap: 3px; flex: 1; min-width: 0; }
      .sdl-dc-rowtitle { font-size: 14px; font-weight: 700; color: white; letter-spacing: 0.3px; }
      .sdl-dc-rowsub { display: flex; align-items: center; gap: 8px; min-width: 0; }
      .sdl-dc-rowdesc {
        font-size: 11px;
        color: rgba(255,255,255,0.45);
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }
      .sdl-dc-pips { display: flex; gap: 3px; flex-shrink: 0; }
      .sdl-dc-pip {
        width: 6px;
        height: 6px;
        border-radius: 50%;
        background: rgba(255,255,255,0.18);
      }
      .sdl-dc-pip.claimed { background: var(--green, #00f5a0); }
      .sdl-dc-pip.ready { background: var(--gold); }

      .sdl-dc-progresswrap {
        display: flex;
        flex-direction: column;
        align-items: flex-end;
        gap: 4px;
        flex-shrink: 0;
        width: 64px;
      }
      .sdl-dc-count { font-family: var(--font-display); font-size: 13px; color: rgba(255,255,255,0.75); }
      .sdl-dc-bar {
        width: 100%;
        height: 4px;
        border-radius: 2px;
        background: rgba(255,255,255,0.12);
        overflow: hidden;
      }
      .sdl-dc-fill { display: block; height: 100%; background: var(--gold); border-radius: 2px; transition: width 0.3s ease; }

      .sdl-dc-collect {
        display: flex;
        align-items: center;
        gap: 5px;
        flex-shrink: 0;
        padding: 6px 10px;
        background: linear-gradient(135deg, rgba(255,215,0,0.25), rgba(255,215,0,0.1));
        border: 1px solid rgba(255,215,0,0.6);
        border-radius: 9px;
        cursor: pointer;
        -webkit-tap-highlight-color: transparent;
      }
      .sdl-dc-collect:active { transform: scale(0.96); }
      .sdl-dc-collect.as-span { pointer-events: none; }
      .sdl-dc-collect img { width: 14px; height: 14px; }
      .sdl-dc-collect span {
        font-family: var(--font-display);
        font-size: 14px;
        color: var(--gold);
      }
      .sdl-dc-collect em {
        font-style: normal;
        font-size: 10px;
        font-weight: 800;
        letter-spacing: 0.8px;
        color: rgba(255,255,255,0.85);
      }
      .sdl-dc-maxed {
        flex-shrink: 0;
        width: 24px;
        text-align: center;
        color: var(--green, #00f5a0);
        font-weight: 700;
      }

      .sdl-dc-foot {
        display: flex;
        align-items: center;
        justify-content: space-between;
        padding: 8px 14px 12px;
        gap: 10px;
      }
      .sdl-dc-dots { display: flex; gap: 6px; }
      .sdl-dc-dot {
        width: 8px;
        height: 8px;
        border-radius: 50%;
        background: rgba(255,255,255,0.2);
        border: none;
        padding: 0;
        cursor: pointer;
        transition: background 0.2s, transform 0.2s;
      }
      .sdl-dc-dot.active { background: var(--gold); transform: scale(1.2); }
      .sdl-dc-dot.complete:not(.active) { background: var(--green, #00f5a0); }
      .sdl-dc-dot.hasclaim:not(.active) { background: rgba(255,215,0,0.7); }
      .sdl-dc-hint {
        font-size: 11px;
        color: rgba(255,255,255,0.5);
        font-weight: 600;
        letter-spacing: 0.3px;
      }
    `;
    document.head.appendChild(styles);
  }

  destroy(): void {
    document.removeEventListener('visibilitychange', this.onVisibilityChange);
    this.element.remove();
    this.removeAllListeners();
  }
}
